import { arrayMove } from 'react-sortable-hoc';
import BaseModel from './BaseModel';
import { asyncFn } from './utils';

const recursive = (oldObj, newObj, mapObj) => {
  mapObj[oldObj.id] = {
    value: oldObj.id,
    label: oldObj.alias,
    image: oldObj.image,
    count: oldObj.count,
    relation_id: oldObj.relation_id,
  };

  newObj = {
    ...newObj,
    value: oldObj.id,
    label: oldObj.alias,
    image: oldObj.image,
    count: oldObj.count,
    relation_id: oldObj.relation_id,
    children: [],
  };

  if (oldObj.relations && oldObj.relations.length) {
    newObj.children = oldObj.relations.map(entry => (recursive(entry, {}, mapObj)).newObj);
  }
  return { newObj, mapObj };
};

class CrudModel extends BaseModel {
  initialState = {
    all: {
      error: false,
      errorMessage: '',
      isLoading: false,
      data: [],
      indexedCategories: {},
      headers: [],
      total: undefined,
    },
    current: {
      error: false,
      errorMessage: '',
      isLoading: false,
      data: {},
    },
    detailed: {
      error: false,
      errorMessage: '',
      isLoading: false,
      data: {
        categoryId: {},
        eventId: {},
      },
    },
  }

  constructor(data = {}) {
    super(data);
    if (data.model) {
      this.model = {
        get: {
          request: this.buildActionName('request', data.model, 'get'),
          response: this.buildActionName('response', data.model, 'get'),
          error: this.buildActionName('error', data.model, 'get'),
        },
        create: {
          request: this.buildActionName('request', data.model, 'create'),
          response: this.buildActionName('response', data.model, 'create'),
          error: this.buildActionName('error', data.model, 'create'),
        },
        update: {
          request: this.buildActionName('request', data.model, 'update'),
          response: this.buildActionName('response', data.model, 'update'),
          error: this.buildActionName('error', data.model, 'update'),
        },
        delete: {
          request: this.buildActionName('request', data.model, 'delete'),
          response: this.buildActionName('response', data.model, 'delete'),
          error: this.buildActionName('error', data.model, 'delete'),
        },
        all: {
          request: this.buildActionName('request', data.model, 'all'),
          response: this.buildActionName('response', data.model, 'all'),
          error: this.buildActionName('error', data.model, 'all'),
        },
        order: {
          request: this.buildActionName('request', data.model, 'order'),
          response: this.buildActionName('response', data.model, 'order'),
          error: this.buildActionName('error', data.model, 'order'),
        },
        getProduct: {
          request: this.buildActionName('request', 'get', 'product', 'detailed'),
          response: this.buildActionName('response', 'get', 'product', 'detailed'),
          error: this.buildActionName('error', 'get', 'product', 'detailed'),
        },
      };
    }
  }

  get = ({ body, type }) => asyncFn({
    body, url: `${this.url}/edit`, type, model: this.model.get,
  })
  create = ({ body }) => asyncFn({
    body, url: `${this.url}/create`, model: this.model.create,
  })
  update = ({ body }) => asyncFn({
    body, url: `${this.url}/update`, model: this.model.update,
  })
  delete = ({ body }) => asyncFn({
    body, url: `${this.url}/remove`, model: this.model.delete,
  })
  all = ({ body } = {}) => asyncFn({
    body, url: `${this.url}/list`, model: this.model.all,
  })
  order = ({ body } = {}) => asyncFn({
    body, url: `${this.url}/order`, model: this.model.order,
  })

  getProduct = ({ body } = {}) => asyncFn({
    body: {
      ...body,
      offset: 1,
      limit: 10,
      sort: 1,
      list_type: 'list',
    },
    url: `https://ubshop.mn/rest/product/all`,
    model: this.model.getProduct,
    extra: {
      type: (body.relation_id || body.relation_id === 0) ? 'category' : 'event',
      eventId: body.events ? body.events[0] : undefined,
      categoryId: body.relation_id,
    },
  })

  changeOrder = props => (dispatch) => {
    dispatch({
      type: 'CHANGE_ORDER',
      ...props,
    });
  }

  reset = () => async (dispatch) => {
    dispatch({
      type: 'FORM_RESET',
    });
  };

  reducer = (state = this.initialState, action) => {
    switch (action.type) {
      case 'CHANGE_ORDER': {
        if (action.col === 1) {
          return {
            ...state,
            all: {
              ...state.all,
              list: arrayMove(state.all.list, action.oldIndex, action.newIndex),
            },
          };
        } else if (action.col === 2) {
          return {
            ...state,
            all: {
              ...state.all,
              list: [
                ...state.all.list.slice(0, action.firstIndex),
                {
                  ...state.all.list[action.firstIndex],
                  relations: arrayMove(state.all.list[action.firstIndex].relations, action.oldIndex, action.newIndex),
                },
                ...state.all.list.slice(action.firstIndex + 1),
              ],
            },
          };
        } else if (action.col === 3) {
          return {
            ...state,
            all: {
              ...state.all,
              list: [
                ...state.all.list.slice(0, action.firstIndex),
                {
                  ...state.all.list[action.firstIndex],
                  relations: [
                    ...state.all.list[action.firstIndex].relations.slice(0, action.secondIndex),
                    {
                      ...state.all.list[action.firstIndex].relations[action.secondIndex],
                      relations: arrayMove(state.all.list[action.firstIndex].relations[action.secondIndex].relations, action.oldIndex, action.newIndex),
                    },
                    ...state.all.list[action.firstIndex].relations.slice(action.secondIndex + 1),
                  ],
                },
                ...state.all.list.slice(action.firstIndex + 1),
              ],
            },
          };
        }
        return {
          ...state,
          all: {
            list: [
              ...state.all.list.slice(0, action.firstIndex),
              {
                ...state.all.list[action.firstIndex],
                relations: [
                  ...state.all.list[action.firstIndex].relations.slice(0, action.secondIndex),
                  {
                    ...state.all.list[action.firstIndex].relations[action.secondIndex],
                    relations: [
                      ...state.all.list[action.firstIndex].relations[action.secondIndex].relations.slice(0, action.thirdIndex),
                      {
                        ...state.all.list[action.firstIndex].relations[action.secondIndex].relations[action.thirdIndex],
                        relations: arrayMove(state.all.list[action.firstIndex].relations[action.secondIndex].relations[action.thirdIndex].relations, action.oldIndex, action.newIndex),
                      },
                      ...state.all.list[action.firstIndex].relations[action.secondIndex].relations.slice(action.thirdIndex + 1),
                    ],
                  },
                  ...state.all.list[action.firstIndex].relations.slice(action.secondIndex + 1),
                ],
              },
              ...state.all.list.slice(action.firstIndex + 1),
            ],
          },
        };
      }
      case 'FORM_RESET':
        console.log('action: ', action);
        return {
          ...state,
          current: {
            error: false,
            errorMessage: '',
            isLoading: false,
            data: {},
          },
        };
      case this.model.all.request:
        return {
          ...state,
          all: this.requestCase(state.all, action),
        };
      case this.model.all.error:
        return {
          ...state,
          all: this.errorCase(state.all, action),
        };
      case this.model.all.response:
        const indexData = {};
        const testcat = action.payload.map(cat => recursive(cat, {}, indexData).newObj);

        return {
          ...state,
          all: {
            ...state.all,
            isLoading: false,
            data: testcat,
            list: action.payload,
            indexedCategories: indexData,
          },
        };
      case this.model.get.request:
        return {
          ...state,
          current: this.requestCase(state.current, action),
        };
      case this.model.get.error:
        return {
          ...state,
          current: this.errorCase(state.current, action),
        };
      case this.model.get.response:
        return {
          ...state,
          current: {
            ...state.current,
            isLoading: false,
            data: action.payload,
            isLogged: true,
          },
        };
      case this.model.create.request:
        return {
          ...state,
          current: this.requestCase(state.current, action),
        };
      case this.model.create.error:
        return {
          ...state,
          current: this.errorCase(state.current, action),
        };
      case this.model.create.response:
        return {
          ...state,
          current: {
            ...state.current,
            isLoading: false,
            data: action.payload,
          },
        };
      case this.model.update.request:
        return {
          ...state,
          current: this.requestCase(state.current, action),
        };
      case this.model.update.error:
        return {
          ...state,
          current: this.errorCase(state.current, action),
        };
      case this.model.update.response:
        return {
          ...state,
          current: {
            ...state.current,
            isLoading: false,
            data: action.payload,
          },
        };
      case this.model.delete.request:
        return {
          ...state,
          current: {
            isLoading: false,
            error: false,
          },
        };
      case this.model.delete.error:
        return {
          ...state,
          current: {
            isLoading: false,
            error: true,
          },
        };
      case this.model.delete.response:
        return {
          ...state,
          current: {
            ...state.current,
            isLoading: false,
            data: {},
          },
        };
      case this.model.order.request:
        return {
          ...state,
          all: this.requestCase(state.all, action),
        };
      case this.model.order.error:
        return {
          ...state,
          all: this.errorCase(state.all, action),
        };
      case this.model.order.response:
        return state;
      case this.model.getProduct.request:
        return {
          ...state,
          detailed: this.requestCase(state.detailed, action),
        };
      case this.model.getProduct.error:
        return {
          ...state,
          detailed: this.errorCase(state.detailed, action),
        };
      case this.model.getProduct.response: {
        if (action.extra.type === 'category') {
          return {
            ...state,
            detailed: {
              ...state.detailed,
              data: {
                ...state.detailed.data,
                categoryId: {
                  ...state.detailed.data.categoryId,
                  [action.extra.categoryId]: action.payload.list,
                },
              },
            },
          };
        }
        return {
          ...state,
          detailed: {
            ...state.detailed,
            data: {
              ...state.detailed.data,
              eventId: {
                ...state.detailed.data.eventId,
                [action.extra.eventId]: action.payload.list,
              },
            },
          },
        };
      }
      default:
        return state;
    }
  }
}

export default CrudModel;



// WEBPACK FOOTER //
// ./src/models/CategoryModel.js